import React, { useEffect, useState } from 'react';
import './EditDemoUser.scss';
import ErrorSign from '../../../../../images/icons/error-sign.svg';
import { useService } from 'react-service-locator';
import { toast } from 'sonner';
import { useParams, useHistory } from 'react-router-dom';
import BreadCrumbs from '../../../../../components/BreadCrumbs/BreadCrumbs';
import ErrorBanner from '../../../../../components/ErrorBanner/ErrorBanner';
import SignedInput from '../../../../../components/SignedInput/SignedInput';
import StyledDropdown from '../../../../../components/StyledDropdown/StyledDropdown';
import { Title } from '../../../../../GeneralModels/Enums/TitleEnum';
import { validateEmail, validatePhoneNumber } from '../../../../../helpers/Validators';
import { UpdateDemoUserDto, UserService } from '../../../../../services/user.service';

const EditDemoUser: React.FC = () => {
    const userService = useService(UserService);
    const history = useHistory();
    const { id } = useParams<{ id: string }>();

    const rawTitles: string[] = [Title[0], Title[1], Title[2]];

    const titles = rawTitles.map((t) => t.replace(/\./g, ''));

    const [generalEmail, setGeneralEmail] = useState('');
    const [generalPhone, setGeneralPhone] = useState('');
    const [company, setCompany] = useState('');
    const [address, setAddress] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userPhone, setUserPhone] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [userTitle, setUserTitle] = useState('');
    const [licenseEnd, setLicenseEnd] = useState('');

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!id) return;

        userService
            .fetchUserById(id)
            .then((user) => {
                setGeneralEmail(user.generalEmail || '');
                setGeneralPhone(user.generalPhone || '');
                setCompany(user.company || '');
                setAddress(user.address || '');
                setUserEmail(user.userEmail || '');
                setUserPhone(user.userPhone || '');
                setFirstName(user.firstName || '');
                setLastName(user.lastName || '');
                setUserTitle(user.title !== undefined ? String(user.title) : '');
                setLicenseEnd(user.licenseEnd ? new Date(user.licenseEnd).toISOString().split('T')[0] : '');
            })
            .catch(() => setError('Failed to load user.'))
            .finally(() => setLoading(false));
    }, [id, userService]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        toast.dismiss();

        if (
            !generalEmail ||
            !generalPhone ||
            !company ||
            !address ||
            !userEmail ||
            !userPhone ||
            !firstName ||
            !lastName ||
            !userTitle ||
            !licenseEnd
        ) {
            setError('Please fill in all fields.');
            return;
        }

        if (!validateEmail(generalEmail) || !validateEmail(userEmail)) {
            setError('Invalid email address.');
            return;
        }

        if (!validatePhoneNumber(generalPhone) || !validatePhoneNumber(userPhone)) {
            setError('Invalid phone number.');
            return;
        }

        const payload: UpdateDemoUserDto = {
            id: id!,
            generalEmail,
            generalPhone,
            company,
            address,
            userEmail,
            userPhone,
            firstName,
            lastName,
            title: Title[userTitle as keyof typeof Title],
            licenseEnd: new Date(`${licenseEnd}T00:00:00`).toISOString(),
        };

        const loadToast = toast.loading('Saving...');
        try {
            await userService.updateDemoUser(payload);
            toast.success('User updated.', { id: loadToast });
            history.push('/admin-settings/dashboard');
        } catch (err: any) {
            toast.error('Failed to update user.', { id: loadToast });
            setError(err?.response?.data?.error || 'Failed to update user.');
        }
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="edit-demo-user">
            <BreadCrumbs
                breadcrumbs={[
                    { to: '/', name: 'Search' },
                    { to: '/admin-settings', name: 'Settings' },
                    { to: '/dashboard', name: 'Dashboard' },
                    { to: '#', name: 'Edit Demo User' },
                ]}
            />

            <div className="error__message">
                {error && <ErrorBanner bannerHeader="Failed" bannerDescription={error} bannerIcon={ErrorSign} />}
            </div>

            {!error && (
                <div className="card">
                    <form className="styled__form" onSubmit={handleSubmit}>
                        <div className="input__card">
                            <div className="client__info">
                                <div className="description__text">Client account</div>
                                <div className="client__inputs">
                                    <SignedInput
                                        label="General email"
                                        inputValue={generalEmail}
                                        setInputValue={setGeneralEmail}
                                        placeholder="example@example.com"
                                        isError={!!error}
                                    />
                                    <SignedInput
                                        label="General phone"
                                        inputValue={generalPhone}
                                        setInputValue={setGeneralPhone}
                                        placeholder="+123456789"
                                        isError={!!error}
                                    />
                                    <SignedInput
                                        label="Company"
                                        inputValue={company}
                                        setInputValue={setCompany}
                                        placeholder="Company"
                                        isError={!!error}
                                    />
                                    <SignedInput
                                        label="Business address"
                                        inputValue={address}
                                        setInputValue={setAddress}
                                        placeholder="Address"
                                        isError={!!error}
                                    />
                                </div>
                            </div>

                            <div className="client__info">
                                <div className="description__text">User account info</div>
                                <div className="client__inputs">
                                    <SignedInput
                                        label="User email"
                                        inputValue={userEmail}
                                        setInputValue={setUserEmail}
                                        placeholder="example@example.com"
                                        isError={!!error}
                                    />
                                    <SignedInput
                                        label="User phone"
                                        inputValue={userPhone}
                                        setInputValue={setUserPhone}
                                        placeholder="+123456789"
                                        isError={!!error}
                                    />
                                    <SignedInput
                                        label="First name"
                                        inputValue={firstName}
                                        setInputValue={setFirstName}
                                        placeholder="First name"
                                        isError={!!error}
                                    />
                                    <SignedInput
                                        label="Last name"
                                        inputValue={lastName}
                                        setInputValue={setLastName}
                                        placeholder="Last name"
                                        isError={!!error}
                                    />

                                    <StyledDropdown
                                        options={titles}
                                        onSelect={setUserTitle}
                                        value={userTitle}
                                        placeHolder="(Mr. / Mrs. / Ms.)"
                                        isError={!!error}
                                        sign="Title"
                                    />

                                    <div className="date-picker">
                                        <label className="sign">License end</label>
                                        <div className="date-field">
                                            <input
                                                type="date"
                                                value={licenseEnd}
                                                onChange={(e) => setLicenseEnd(e.target.value)}
                                                className="date-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <button type="submit" className="button__submit button__submit-ready">
                            Save
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default EditDemoUser;
